import { Slot } from "@radix-ui/react-slot";
import { type ComponentPropsWithRef, type ReactNode, useId } from "react";
import { cn } from "../../utils/classes";
import styles from "./page-section.module.css";

export type PageSectionProps = Omit<ComponentPropsWithRef<"section">, "title"> & {
  title?: ReactNode;
  description?: ReactNode;
  /** Already-resolved total, formatted in en-GB. Zero is shown; undefined is omitted. */
  count?: number;
  badges?: ReactNode;
  actions?: ReactNode;
  /** An overlay such as a loading bar, positioned relative to the whole section. */
  overlay?: ReactNode;
  /** Wrap ordinary content in the standard inset; tables can remain edge to edge. */
  padded?: boolean;
};

/** A themed page surface with an optional heading, inline help and trailing actions. */
export function PageSection({ title, description, count, badges, actions, overlay, padded = false, className, children, ...props }: PageSectionProps) {
  const headingId = useId();
  const descriptionId = useId();
  const hasTitle = title !== undefined && title !== null;
  const hasDescription = description !== undefined && description !== null;
  const hasCount = count !== undefined;
  return (
    <section
      aria-labelledby={hasTitle ? headingId : undefined}
      aria-describedby={hasDescription ? descriptionId : undefined}
      {...props}
      className={cn(styles.section, className)}
    >
      {overlay}
      {(hasTitle || hasDescription || hasCount || badges != null || actions != null) && (
        <header className={styles.header}>
          <div className={styles.heading}>
            {(hasTitle || hasCount) && (
              <div className={styles.titleGroup}>
                {hasTitle && <h2 id={headingId} className={styles.title}>{title}</h2>}
                {hasCount && <PageSectionBadge>{count.toLocaleString("en-GB")}</PageSectionBadge>}
              </div>
            )}
            {badges}
            {hasDescription && <div id={descriptionId} className={styles.description}>{description}</div>}
          </div>
          {actions != null && <div className={styles.actions}>{actions}</div>}
        </header>
      )}
      {padded ? <div className={styles.content}>{children}</div> : children}
    </section>
  );
}

/** Use asChild with a router Link or anchor to preserve native navigation behaviour. */
export type PageSectionActionProps = ComponentPropsWithRef<"button"> & {
  asChild?: boolean;
  "aria-label": string;
};

export function PageSectionAction({ asChild = false, className, ...props }: PageSectionActionProps) {
  const Component = asChild ? Slot : "button";
  return <Component {...(!asChild ? { type: "button" as const } : {})} {...props} className={cn(styles.action, className)} />;
}

export type PageSectionBadgeProps = ComponentPropsWithRef<"span">;

export function PageSectionBadge({ className, ...props }: PageSectionBadgeProps) {
  return <span {...props} className={cn(styles.badge, className)} />;
}
